import { AnimatePresence, motion } from "motion/react";
import { CalendarDays, Check, ChevronDown, CircleCheck, Hand, Images, ListChecks, Upload } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DayBoard } from "../../components/DayBoard";
import { Button } from "../../components/ui/button";
import { Avatar, Segmented, StatusBadge } from "../../components/ui/primitives";
import { ME_EDITOR, PEOPLE } from "../../data/people";
import { spring } from "../../lib/motion";
import { ago, dayName, dayOf, dayOn, timeOf } from "../../lib/time";
import { useNow } from "../../lib/useNow";
import { cn, plural } from "../../lib/utils";
import { liveItems, tasksLabel } from "../../lib/domain";
import { MONTAGE_PATHS, useStore } from "../../store";
import type { Status, Task } from "../../types";
import { useEditorActions, WithdrawSubmission } from "./EditorShared";
import { AccountPosts } from "./AccountPosts";
import { OwnTopicButton } from "./OwnTopic";
import { PushPrompt } from "../../components/PushPrompt";

/** Задача у монтажёра открывается модалкой поверх списка — вариант A из прототипа. */
export function useOpenEditorTask() {
  const openTask = useStore((s) => s.openTask);
  const setIntent = useStore((s) => s.setIntent);
  return (id: string, mode?: "propose" | "upload", origin = "etask") => {
    setIntent(mode ? { id, mode } : null);
    openTask(id, origin);
  };
}

const BAND: Record<Status, string> = {
  open: "shadow-[inset_3px_0_0_0_var(--ring),0_1px_2px_rgb(10_10_10/0.06)]",
  publish: "shadow-[inset_3px_0_0_0_var(--publish),0_1px_2px_rgb(10_10_10/0.06)]",
  wait: "shadow-[inset_3px_0_0_0_var(--wait),0_1px_2px_rgb(10_10_10/0.06)]",
  work: "shadow-[inset_3px_0_0_0_var(--work),0_1px_2px_rgb(10_10_10/0.06)]",
  review: "shadow-[inset_3px_0_0_0_var(--review),0_1px_2px_rgb(10_10_10/0.06)]",
  rework: "shadow-[inset_3px_0_0_0_var(--rework),0_1px_2px_rgb(10_10_10/0.06)]",
  done: "shadow-[inset_3px_0_0_0_var(--done),0_1px_2px_rgb(10_10_10/0.06)]",
};

function Row({ task, meta, metaClass, right, onOpen }: { task: Task; meta: string; metaClass?: string; right?: ReactNode; onOpen: () => void }) {
  const n = liveItems(task).length;
  return (
    <motion.div
      layoutId={`etask-${task.id}`}
      layout
      transition={spring.gentle}
      style={{ borderRadius: 20 }}
      onClick={onOpen}
      className={cn("flex cursor-pointer items-center gap-3 bg-card py-3 pl-[17px] pr-3 transition-colors hover:bg-sidebar", BAND[task.status])}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-sm font-medium">{task.title}</span>
        <span className={cn("truncate text-xs text-muted-foreground", metaClass)}>
          {meta}
          {n > 1 && <span className="text-muted-foreground"> · {tasksLabel(n)}</span>}
        </span>
      </div>
      {right && <div onClick={(e) => e.stopPropagation()}>{right}</div>}
    </motion.div>
  );
}

/** `onOpen` после «Взять в работу» — окно публикации на сдаче. */
function Featured({ task, now, onOpen, onPropose }: { task: Task; now: number; onOpen: () => void; onPropose: () => void }) {
  const actions = useEditorActions();
  const creator = PEOPLE[task.creator];
  return (
    <motion.div
      layoutId={`etask-${task.id}`}
      layout
      transition={spring.gentle}
      style={{ borderRadius: 20 }}
      onClick={onOpen}
      className="flex cursor-pointer flex-col gap-3 bg-card p-4 shadow-[inset_3px_0_0_0_var(--wait),0_1px_2px_rgb(10_10_10/0.05),0_8px_22px_rgb(245_158_11/0.16)]"
    >
      <div className="flex items-center gap-2">
        <Avatar id={task.creator} size={22} />
        <span className="text-[13px] font-medium">{creator.name}</span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {ago(task.createdAt, now) === "только что" ? "только что" : `${ago(task.createdAt, now)} назад`}
        </span>
        {task.proposal && <StatusBadge status="review">Ждём ответа</StatusBadge>}
      </div>
      <span className="text-base font-semibold tracking-[-0.01em]">{task.title}</span>
      <span className="flex items-center gap-2 text-[13px]">
        <CalendarDays className="size-3.5 text-muted-foreground" />
        {task.proposal
          ? `Вы предложили ${dayOn(task.proposal.day, now)}, до ${timeOf(task.proposal.due)}`
          : `${dayName(task.day, now)} · до ${timeOf(task.due)}`}
      </span>
      {!task.proposal && (
        <div className="flex gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
          <Button className="flex-1" onClick={() => { actions.accept(task); onOpen(); }}>
            <Check />
            Взять в работу
          </Button>
          <Button variant="outline" onClick={onPropose}>
            Не успеваю
          </Button>
        </div>
      )}
    </motion.div>
  );
}

function Section({ title, dot, count, children }: { title: string; dot: string; count: number; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-6 items-center gap-2 px-1">
        <span className={cn("size-2 rounded-full", dot)} />
        <span className="text-sm font-semibold">{title}</span>
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold text-muted-foreground">{count}</span>
      </div>
      {children}
    </div>
  );
}

/** Список задач монтажёра: сначала то, что требует ответа, потом — что сдать сегодня, потом — остальная неделя */
export function TaskList({ className }: { className?: string }) {
  const now = useNow();
  const tasks = useStore((s) => s.tasks);
  const open = useOpenEditorTask();
  const [doneOpen, setDoneOpen] = useState(false);
  const today = dayOf(now);
  const take = useStore((s) => s.take);
  const go = useNavigate();
  const mine = tasks.filter((t) => t.editor === ME_EDITOR);
  // Свободные темы команды: никто не взял, можно взять себе.
  const free = tasks.filter((t) => t.status === "open").sort((a, b) => a.day - b.day || b.createdAt - a.createdAt);
  const waits = mine.filter((t) => t.status === "wait").sort((a, b) => a.day - b.day || b.createdAt - a.createdAt);
  const todays = mine.filter((t) => (t.status === "work" || t.status === "rework") && t.day <= today).sort((a, b) => a.due - b.due);
  const later = mine.filter((t) => t.status === "work" && t.day > today).sort((a, b) => a.due - b.due);
  const reviews = mine.filter((t) => t.status === "review" || t.status === "publish");
  const dones = mine.filter((t) => t.status === "done");

  const submitBtn = (t: Task) => (
    <Button variant="outline" size="sm" onClick={() => open(t.id, "upload")}>
      <Upload />
      {t.status === "rework" ? "Доработать" : liveItems(t).length ? "Сдать" : "Добавить видео"}
    </Button>
  );

  return (
    <div className={cn("no-scrollbar flex min-h-0 flex-col gap-[22px] overflow-y-auto pb-20 pr-1", className)}>
      {free.length > 0 && (
        <Section title="Свободные публикации команды" dot="bg-ring" count={free.length}>
          {free.map((t) => (
            <Row
              key={t.id}
              task={t}
              meta={`${t.period === "week" ? "на неделю · " : ""}${dayName(t.day, now)} · от ${PEOPLE[t.creator].name}`}
              right={
                <Button size="sm" onClick={async () => { await take(t.id); open(t.id, "upload"); }}>
                  <Hand />
                  Взять себе
                </Button>
              }
              onOpen={() => open(t.id)}
            />
          ))}
        </Section>
      )}
      {waits.length > 0 && (
        <Section title="Новые" dot="bg-wait" count={waits.length}>
          {waits.map((t, i) =>
            i === 0 ? (
              <Featured key={t.id} task={t} now={now} onOpen={() => open(t.id, "upload")} onPropose={() => open(t.id, "propose")} />
            ) : (
              <Row
                key={t.id}
                task={t}
                meta={t.proposal ? `вы предложили ${dayOn(t.proposal.day, now)}` : `${dayName(t.day, now)} · до ${timeOf(t.due)}`}
                metaClass={t.proposal ? "text-review-fg" : undefined}
                onOpen={() => open(t.id)}
              />
            ),
          )}
        </Section>
      )}
      <Section title="Сдать сегодня" dot="bg-work" count={todays.length}>
        {todays.map((t) => {
          const cnt = t.items.reduce((n, i) => n + (i.status === "rework" ? i.revisions.filter((r) => r.round === i.round - 1).length : 0), 0);
          const late = now > t.due;
          return (
            <Row
              key={t.id}
              task={t}
              meta={
                t.status === "rework"
                  ? `правки · ${cnt} ${plural(cnt, ["правка", "правки", "правок"])} · до ${timeOf(t.due)}`
                  : late
                    ? `срок был до ${timeOf(t.due)}`
                    : `до ${timeOf(t.due)}`
              }
              metaClass={t.status === "rework" || late ? "text-rework-fg" : "text-work-fg"}
              right={submitBtn(t)}
              onOpen={() => open(t.id)}
            />
          );
        })}
        {todays.length === 0 && <div className="px-1 text-xs text-muted-foreground">На сегодня всё сдано</div>}
      </Section>
      {later.length > 0 && (
        <Section title="Дальше на неделе" dot="bg-ring" count={later.length}>
          {later.map((t) => (
            <Row key={t.id} task={t} meta={`${dayName(t.day, now)} · до ${timeOf(t.due)}`} onOpen={() => open(t.id)} />
          ))}
        </Section>
      )}
      {reviews.length > 0 && (
        <Section title="На проверке" dot="bg-review" count={reviews.length}>
          {reviews.map((t) => (
            <Row
              key={t.id}
              task={t}
              meta={(() => {
                const checking = t.items.filter((i) => i.status === "uploaded" || i.status === "moderated").length;
                const linking = t.items.filter((i) => i.status === "approved").length;
                if (checking) return `${checking} ${plural(checking, ["видео ждёт", "видео ждут", "видео ждут"])} проверки`;
                return `принято ${linking} · ждёт ссылки на пост`;
              })()}
              right={(() => {
                const sent = liveItems(t).filter((i) => i.status === "uploaded");
                if (sent.length === 1) return <WithdrawSubmission task={t} item={sent[0]} />;
                // Принято, а ссылки нет — пост ищется в ленте аккаунта ролика.
                const waiting = liveItems(t).find((i) => i.status === "approved");
                if (waiting) {
                  const q = new URLSearchParams({ item: waiting.id, ...(waiting.accountId ? { account: waiting.accountId } : {}) });
                  return <Button size="sm" variant="outline" onClick={() => go(`${MONTAGE_PATHS.editorPosts}?${q}`)}>Найти пост</Button>;
                }
                return undefined;
              })()}
              onOpen={() => open(t.id)}
            />
          ))}
        </Section>
      )}
      {dones.length > 0 && (
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => setDoneOpen((o) => !o)} className="flex h-8 items-center gap-2 rounded-full px-1 text-left hover:text-muted-foreground">
            <CircleCheck className="size-4 text-done" />
            <span className="text-sm font-semibold">Выполнено на неделе</span>
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold text-muted-foreground">{dones.length}</span>
            <motion.span animate={{ rotate: doneOpen ? 180 : 0 }} transition={spring.snappy}>
              <ChevronDown className="size-4 text-muted-foreground" />
            </motion.span>
          </button>
          <AnimatePresence initial={false}>
            {doneOpen && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="flex flex-col gap-2 overflow-hidden">
                {dones.map((t) => (
                  <Row key={t.id} task={t} meta={dayName(t.day, now)} onOpen={() => open(t.id)} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

/** Неделя монтажёра по дням. Новую задачу можно перетащить на другой день — это предложение постановщику */
export function MyWeek({
  className,
  compact,
  fromToday,
  highlightId,
  onOpen,
}: {
  className?: string;
  compact?: boolean;
  fromToday?: boolean;
  highlightId?: string;
  onOpen?: (task: Task, el: HTMLElement) => void;
}) {
  const now = useNow();
  const tasks = useStore((s) => s.tasks);
  const busy = useStore((s) => s.busy);
  const open = useOpenEditorTask();
  const actions = useEditorActions();
  return (
    <DayBoard
      className={className}
      compact={compact}
      fromToday={fromToday}
      now={now}
      editor={ME_EDITOR}
      tasks={tasks}
      busy={busy}
      highlightId={highlightId}
      onOpen={(t, el) => (onOpen ? onOpen(t, el) : open(t.id, undefined, "eday"))}
      onPropose={(t, day) => actions.propose(t, day, t.due + (day - t.day) * 1440, `${dayName(t.day, now)} не успеваю — могу ${dayOn(day, now)}`)}
    />
  );
}

export function MyTasks({ view }: { view: "tasks" | "calendar" | "posts" }) {
  const navigate = useStore((s) => s.navigate);

  return (
    <div className="flex h-full min-h-0 flex-col gap-5">
      <PushPrompt />
      <header className="flex items-center gap-4">
        <h1 className="sr-only">
          {view === "tasks" ? "Мои задачи" : view === "posts" ? "Посты аккаунтов" : "Календарь"}
        </h1>
        <Segmented
          id="editor-view"
          value={view}
          onChange={(next) => navigate({ name: next === "calendar" ? "editorCalendar" : next === "posts" ? "editorPosts" : "myTasks" })}
          options={[
            { value: "tasks", label: "Публикации", icon: <ListChecks /> },
            { value: "calendar", label: "Календарь", icon: <CalendarDays /> },
            // Лента аккаунтов: приложить ссылку от поста к ролику (23.10.2026).
            { value: "posts", label: "Посты", icon: <Images /> },
          ]}
        />
        <span className="flex-1" />
        <OwnTopicButton />
      </header>
      {view === "posts" ? (
        <AccountPosts className="min-h-0 min-w-0 flex-1" />
      ) : view === "calendar" ? (
        <MyWeek className="min-h-0 min-w-0 flex-1" />
      ) : (
        <div className="flex min-h-0 flex-1 gap-5">
          <TaskList className="w-[380px] shrink-0" />
          <MyWeek className="min-w-0 flex-1" fromToday />
        </div>
      )}
    </div>
  );
}
